import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'motion/react'
import { ArrowRight, Check, Clock3, Crown, Gem, Gift, LockKeyhole, Sparkles, X } from 'lucide-react'
import useAppStore from '../store/useAppStore'
import useUiOverlayStore from '../store/useUiOverlayStore'
import useAudioManager from '../hooks/useAudioManager'
import { hapticImpact } from '../utils/haptic'

const STEPS = {
  case: { label: 'case', path: '/cases', icon: Gift },
  upgrader: { label: 'upgrader', path: '/upgrader', icon: Sparkles },
  contract: { label: 'contract', path: '/contract', icon: LockKeyhole },
  classic: { label: 'classic', path: '/royale', icon: Crown },
  double: { label: 'double', path: '/double', icon: Sparkles },
  wheel50: { label: 'wheel50', path: '/x50', icon: Crown },
  casebattle: { label: 'casebattle', path: '/battles', icon: Gift },
  crash: { label: 'crash', path: '/crash', icon: Sparkles },
  team: { label: 'team', path: '/teams', icon: Crown }
}

const formatTime = (seconds) => {
  const remaining = Math.max(0, seconds)
  const hours = String(Math.floor(remaining / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0')
  const secs = String(remaining % 60).padStart(2, '0')
  return `${hours}:${minutes}:${secs}`
}

const RewardVisual = ({ reward, large = false }) => (
  <div className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-amber-300/20 bg-[radial-gradient(circle_at_50%_65%,rgba(251,191,36,0.22),transparent_70%)] ${large ? 'h-44 w-full' : 'h-28 w-28 sm:h-36 sm:w-36'}`}>
    <div className="absolute inset-3 rounded-full border border-amber-300/10" />
    <div className="absolute inset-6 rounded-full border border-amber-300/10" />
    {reward?.image ? (
      <img src={reward.image} alt={reward.name || ''} className="relative z-10 h-[85%] w-[92%] object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.7)]" />
    ) : (
      <Gift size={large ? 64 : 40} className="relative z-10 text-amber-200/70" />
    )}
    <Sparkles size={15} className="absolute right-3 top-3 text-amber-300" />
  </div>
)

export const OnboardingPlus = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const onboarding = useAppStore(state => state.user?.onboarding_plus)
  const loadProfile = useAppStore(state => state.loadProfile)
  const { playSound } = useAudioManager({ select: 'select' })
  const [remaining, setRemaining] = useState(() => Math.max(0, Number(onboarding?.expires_in_sec) || 0))

  useEffect(() => {
    if (onboarding?.status !== 'active') return undefined
    const deadline = Date.now() + Math.max(0, Number(onboarding.expires_in_sec) || 0) * 1000
    const tick = () => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setRemaining(next)
      if (next === 0) {
        clearInterval(timer)
        if (Number(onboarding.expires_in_sec) > 0) loadProfile().catch(() => {})
      }
    }
    const timer = setInterval(tick, 1000)
    tick()
    return () => clearInterval(timer)
  }, [onboarding, loadProfile])

  if (onboarding?.status !== 'active' || remaining <= 0) return null

  const steps = Array.isArray(onboarding.steps) ? onboarding.steps : []
  const done = Math.max(0, Number(onboarding.done) || 0)
  const total = Math.max(0, Number(onboarding.total) || 0)
  const nextStep = steps.find(step => !step.done && STEPS[step.key])
  const progress = total ? Math.min(100, Math.round(done / total * 100)) : 0
  const urgent = remaining < 3600
  const open = (path) => {
    playSound('select')
    hapticImpact('light')
    navigate(path)
  }

  return (
    <section aria-label={t('onboardingPlus.title')} className="relative overflow-hidden rounded-[24px] border border-amber-300/35 bg-surface shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_20px_50px_-16px_rgba(0,0,0,0.7)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_85%_0%,rgba(251,191,36,0.14),transparent_48%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.25)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.25)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:linear-gradient(to_bottom,black,transparent_65%)]" />
      <div className="relative p-4 sm:p-6 lg:p-7">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-300/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.17em] text-amber-200">
            <Sparkles size={12} /> {t('onboardingPlus.badge')}
          </span>
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-xs font-bold tabular-nums ${urgent ? 'border-red-400/40 bg-red-500/10 text-red-300' : 'border-white/15 bg-white/5 text-white'}`}>
            <Clock3 size={13} /> {formatTime(remaining)}
          </span>
        </div>

        <div className="mt-5 flex items-center gap-3 sm:gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-200/80">{t('onboardingPlus.eyebrow')}</p>
            <h2 className="mt-1 text-2xl font-black uppercase leading-[1.05] text-white sm:text-4xl">{t('onboardingPlus.title')}</h2>
            <p className="mt-2 text-xs leading-relaxed text-text-secondary sm:text-sm">{t('onboardingPlus.subtitle')}</p>
            <p className="mt-3 line-clamp-2 text-sm font-bold leading-snug text-amber-200">{onboarding.reward?.name || t('onboardingPlus.rewardPending')}</p>
            {onboarding.reward?.crystals != null && (
              <span className="mt-2 inline-flex items-center gap-1 rounded-lg border border-accent-primary/25 bg-accent-primary/10 px-2 py-1 text-xs font-bold tabular-nums text-accent-primary">
                <Gem size={13} /> {Number(onboarding.reward.crystals).toLocaleString('en-US')}
              </span>
            )}
          </div>
          <RewardVisual reward={onboarding.reward} />
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-dark-primary/75 p-4 sm:p-5">
          <div className="flex items-end justify-between gap-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-text-secondary">{t('onboardingPlus.progress')}</p>
              <p className="mt-1 text-sm font-semibold text-white">{t('onboardingPlus.completed', { done, total })}</p>
            </div>
            <span className="text-3xl font-black tabular-nums text-accent-primary">{progress}%</span>
          </div>
          <div role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} aria-label={t('onboardingPlus.progress')} className="mt-3 flex h-2.5 gap-1 overflow-hidden rounded-full">
            {steps.map((step, index) => (
              <span key={`${step.key}-${index}`} className={`h-full min-w-0 flex-1 rounded-full transition-colors duration-500 ${step.done ? 'bg-gradient-to-r from-accent-primary to-accent-secondary shadow-[0_0_12px_rgba(0,255,127,0.45)]' : 'bg-white/10'}`} />
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-white">{t('onboardingPlus.route')}</p>
          <span className="text-[10px] font-medium text-text-secondary">{t('onboardingPlus.anyOrder')}</span>
        </div>
        <div className="mt-3 grid gap-2 lg:grid-cols-2">
          {steps.map((step, index) => {
            const config = STEPS[step.key]
            const Icon = config?.icon || LockKeyhole
            const isNext = step === nextStep
            const content = (
              <>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${step.done ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary' : isNext ? 'border-amber-300/50 bg-amber-300/15 text-amber-200' : 'border-white/10 bg-white/5 text-text-secondary'}`}>
                  {step.done ? <Check size={18} strokeWidth={3} /> : <Icon size={18} />}
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary">{t('onboardingPlus.step', { number: index + 1 })}</span>
                  <span className={`block text-sm font-semibold ${step.done ? 'text-white/60' : 'text-white'}`}>{t(`onboardingPlus.steps.${config?.label || 'unknown'}`)}</span>
                </span>
                {step.done ? <span className="shrink-0 text-[10px] font-bold uppercase text-accent-primary">{t('onboardingPlus.done')}</span> : config && <ArrowRight size={17} className={isNext ? 'shrink-0 text-amber-200' : 'shrink-0 text-text-secondary'} />}
              </>
            )
            const className = `flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 ${step.done ? 'border-accent-primary/15 bg-accent-primary/5' : isNext ? 'border-amber-300/45 bg-amber-300/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]' : 'border-white/10 bg-white/[0.03]'}`
            return config && !step.done ? (
              <button key={`${step.key}-${index}`} type="button" onClick={() => open(config.path)} className={`${className} transition-all hover:border-amber-300/40 active:scale-[0.98]`}>
                {content}
              </button>
            ) : <div key={`${step.key}-${index}`} className={className}>{content}</div>
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
          <p className="flex items-center gap-2 text-xs text-text-secondary"><Gift size={15} className="shrink-0 text-amber-200" /> {t('onboardingPlus.autoReward')}</p>
          {nextStep && <button type="button" onClick={() => open(STEPS[nextStep.key].path)} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-4 py-2.5 text-xs font-black text-dark-primary shadow-[0_8px_20px_-8px_rgba(0,255,127,0.5)] transition-all hover:opacity-90 active:scale-[0.98]">{t('onboardingPlus.continue')} <ArrowRight size={16} /></button>}
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-text-secondary">{t('onboardingPlus.delayHint')} {t('onboardingPlus.withdrawHint')}</p>
      </div>
    </section>
  )
}

export const OnboardingPlusAward = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const award = useAppStore(state => state.onboardingPlusAward)
  const dismiss = useAppStore(state => state.dismissOnboardingPlusAward)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (!award) return undefined
    const { pushOverlay, popOverlay } = useUiOverlayStore.getState()
    pushOverlay()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = event => { if (event.key === 'Escape') dismiss() }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      popOverlay()
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [award, dismiss])

  if (!award) return null

  return createPortal(
    <div role="presentation" onClick={dismiss} className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/90 p-4">
      <motion.div role="dialog" aria-modal="true" aria-label={t('onboardingPlus.awardedTitle')} onClick={event => event.stopPropagation()} initial={reduceMotion ? false : { opacity: 0, scale: 0.9, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.35 }} className="relative my-auto w-full max-w-sm overflow-hidden rounded-[28px] border border-amber-300/50 bg-surface p-6 text-center shadow-[0_24px_80px_-20px_rgba(251,191,36,0.35)]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-[radial-gradient(ellipse_at_50%_0%,rgba(251,191,36,0.24),transparent_75%)]" />
        <button type="button" onClick={dismiss} aria-label={t('common.close')} className="absolute right-4 top-4 z-10 rounded-full p-2 text-text-secondary transition-colors hover:text-white active:scale-[0.98]"><X size={20} /></button>
        <div className="relative mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-300/15 text-amber-200"><Crown size={26} /></div>
        <p className="relative text-[11px] font-black uppercase tracking-[0.2em] text-amber-200">{t('onboardingPlus.badge')}</p>
        <h2 className="relative mt-2 text-3xl font-black uppercase text-white">{t('onboardingPlus.awardedTitle')}</h2>
        <p className="relative mt-2 text-sm text-text-secondary">{t('onboardingPlus.awardedSubtitle')}</p>
        <div className="relative mt-5"><RewardVisual reward={award.reward} large /></div>
        <p className="relative mt-4 text-base font-bold text-white">{award.reward?.name || t('onboardingPlus.rewardPending')}</p>
        {award.reward?.crystals != null && <p className="relative mt-2 flex items-center justify-center gap-1 font-bold tabular-nums text-accent-primary"><Gem size={16} /> {Number(award.reward.crystals).toLocaleString('en-US')}</p>}
        <button type="button" onClick={() => { dismiss(); navigate('/inventory') }} className="relative mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-5 py-3.5 font-bold text-dark-primary transition-all hover:opacity-90 active:scale-[0.98]">{t('onboardingPlus.inventory')} <ArrowRight size={18} /></button>
      </motion.div>
    </div>,
    document.body
  )
}
